import React, { useCallback, useState, useEffect, useRef, useContext } from 'react';
import { shallowEqual } from 'react-redux';
import { BROWSER_TYPES, MOBILE_OS } from 'utils/constants';
import { PreviewPlayerContext } from 'components/PreviewPlayer';
import MuteButton from 'components/MuteButton';
import { useSelector } from 'reducers';
import storage from 'utils/storage';
import { useIsKeyboardNavigation } from 'utils/useA11yNavigation';
import type MagineMediaPlayer from '@tvoli/hipster-player';
import { VideoContainer, MuteBtnContainer } from './Styles';
import playPriorityManager from './playPriorityManager';

export const VIDEO_ANIMATION_DURATION = 300;

interface PreviewPlayerProps {
  toggleImg: (state: boolean) => void;
  onTrailerEnd: () => void;
  important?: boolean;
  trailerUrl?: string;
  preferredBitrate?: string;
  trailers: {
    id: string;
    defaultPlayable: {
      id: string;
    }
  }[];
  zoom?: number;
}

const getPlayPauseControls = (
  player?: MagineMediaPlayer | HTMLVideoElement, important?: boolean,
) => ({
  play: () => void player?.play(),
  pause: () => void player?.pause(),
  important,
});

export default function PreviewPlayer(props: PreviewPlayerProps): JSX.Element {
  const {
    trailerUrl,
    trailers,
    toggleImg,
    onTrailerEnd,
    important,
    zoom = 100,
    preferredBitrate = 'medium',
  } = props;
  const isKeyboardNav = useIsKeyboardNavigation();
  const [player, setPlayer] = useState<MagineMediaPlayer | undefined>();
  const playerRef = useRef<MagineMediaPlayer | undefined>();
  const playerId = useRef(`PreviewPlayer${Date.now()}`);
  const nativeVideoRef = useRef<HTMLVideoElement>(null);
  const videoContainer = useRef<undefined | HTMLElement>();
  const updateVolume = useRef(() => {});
  const [visible, setVisible] = useState<boolean>(false);
  const [loadVideo, setLoadVideo] = useState<boolean>(false);
  const isBuffering = useRef(false);
  const { muted, toggleMute, pauseMiniPlayer } = useContext(PreviewPlayerContext);
  const {
    apiBaseUri,
    clientApiToken,
    sessionToken,
    defaultAudioTrack,
    userId,
    partner,
    brandName,
    browser,
    isMobile,
  } = useSelector(state => ({
    apiBaseUri: state.settings.apiBaseUri,
    clientApiToken: state.settings.clientApiToken,
    sessionToken: state.auth.sessionToken,
    defaultAudioTrack: state.settings.features.player.defaultAudioTrack,
    userId: state.auth.userId,
    partner: state.settings.partner,
    brandName: state.settings.brand.name,
    browser: state.common.browser,
    isMobile: MOBILE_OS.includes(state.common.os),
  }), shallowEqual);

  playerRef.current = player;
  const isSafari = browser === BROWSER_TYPES.SAFARI;

  // Changes volume directly on video element and avoids storage
  updateVolume.current = () => {
    if (player?.playerElement) {
      const videoEl = player.playerElement as HTMLVideoElement;
      videoEl.volume = muted ? 0 : 1;
    }
  };

  const play = (viewableId: string, playableId: string) => {
    if (!player) return;

    void player.loadWithPreflight({
      accessToken: clientApiToken,
      ads: false,
      assetId: playableId,
      viewableId,
      authorizationKey: sessionToken,
      playbackStartTime: 0,
      preferredBitrate,
      ...(isSafari ? { preferredText: 'off' } : null),
      disablePlaybackSettings: true,
    });
  };

  const muteHandler = useCallback((e: React.MouseEvent) => {
    toggleMute();

    e.stopPropagation();
    e.preventDefault();
  }, [player]);

  const handleClose = useCallback((callOnEnd: any) => {
    if (!player?.model.assetId) {
      return;
    }

    if (player && !player?.currentPlayer?.videoPlayer) {
      player.addEventListener('playerisinitialized', function handler() {
        void player.destroy();
        player.removeEventListener('playerisinitialized', handler);
      });
    } else if (player) {
      void player.destroy();
    }

    playPriorityManager.remove(videoContainer.current);

    toggleImg(true);
    setVisible(false);
    if (callOnEnd && onTrailerEnd) onTrailerEnd();
  }, [player]);

  const onPlayHandler = useCallback(() => {
    pauseMiniPlayer();
    setVisible(true);
    toggleImg(false);

    // play/pause video depends on players' visibility and importance
    playPriorityManager.add(
      videoContainer.current,
      getPlayPauseControls(
        playerRef.current || videoContainer.current!.firstElementChild as HTMLVideoElement,
        important,
      ),
    );
  }, []);

  const initPlayer = (node: HTMLElement) => {
    if (player) {
      return player;
    }

    // eslint-disable-next-line global-require, @typescript-eslint/no-var-requires
    const HipsterPlayer = require('@tvoli/hipster-player').default;

    const hipsterPlayer = new HipsterPlayer({
      baseUri: apiBaseUri,
      logLevel: __PRODUCTION__ ? 1 : 10,
      preferredAudio: storage.getItem('audioTrack') || defaultAudioTrack,
      playerContainer: node,
      userId,
      partner,
      brandName,
      disableServices: {
        fullscreen: true,
        chromecast: true,
        dai: true,
        aspectratio: true,
      },
    });
    // do not interact with the stored volume
    hipsterPlayer.currentPlayer.changeVolume = () => updateVolume.current();

    setPlayer(hipsterPlayer);

    return hipsterPlayer;
  };

  useEffect(() => {
    if (!videoContainer.current) return () => null;

    const el = videoContainer.current; // remember element to remove
    return () => playPriorityManager.remove(el);
  }, [!videoContainer.current]);

  // mute/unmute all Preview HP
  useEffect(() => {
    updateVolume.current();
  }, [muted]);

  useEffect(() => {
    const video = nativeVideoRef.current;
    if (!video) return;
    if (isKeyboardNav) {
      video.pause();
      toggleImg(true);
      setVisible(false);
    } else {
      video.play().catch(() => {});
    }
  }, [isKeyboardNav]);

  useEffect(() => {
    if (!player) return;
    if (isKeyboardNav) {
      player.pause();
      toggleImg(true);
      setVisible(false);
    } else if (!player.model.assetId && trailers?.length) {
        const trailer = trailers[0];
        play(trailer.id, trailer.defaultPlayable.id);
      } else {
        player.play();
      }
  }, [isKeyboardNav, player, trailers]);

  useEffect(() => {
    if (player) {
      const onPlay = () => {
        if (!isBuffering.current && player.model.isPlaying) {
          onPlayHandler();
        }
      };
      const onBuffering = () => {
        // `model.isBuffering` prop is sometimes incorrect
        // but correct one when `buffering` event fired
        isBuffering.current = player.model.isBuffering;
        onPlay();
      };

      player.addEventListener('buffering', onBuffering);
      player.addEventListener('playing', onPlay);
      player.addEventListener('streamEnded', handleClose);

      return () => {
        player.removeEventListener('buffering', onBuffering);
        player.removeEventListener('playing', onPlay);
        player.removeEventListener('streamEnded', handleClose);
        handleClose(false);
      };
    }

    return () => {
      toggleImg(true);
    };
  }, [player]);

  useEffect(() => {
    if (trailerUrl) {
      // load hosted after end of show HS animation
      const timeout = setTimeout(() => setLoadVideo(true), VIDEO_ANIMATION_DURATION);
      return () => clearTimeout(timeout);
    }
    if (trailers?.length) {
      // load linked
      setLoadVideo(true);
    }
    return () => {};
  }, []);

  const playerContainerRef = useCallback((node: HTMLElement) => {
    if (node) {
      initPlayer(node);
      videoContainer.current = node;
    }
  }, []);

  return (
    <>
      { // Hosted trailer (more prio)
        loadVideo && trailerUrl && (
          <VideoContainer
            className="previewPlayer"
            id={playerId.current}
            innerRef={videoContainer}
            zoom={zoom}
            data-nosnippet
          >
            <video
              ref={nativeVideoRef}
              autoPlay={!isKeyboardNav}
              muted={muted}
              playsInline
              disablePictureInPicture
              disableRemotePlayback
              controlsList="nodownload"
              onPlay={onPlayHandler}
              onEnded={handleClose}
            >
              <source src={trailerUrl} />
            </video>
          </VideoContainer>
        )
      }
      { // Linked trailer[0]
        loadVideo && trailers?.length > 0 && !trailerUrl && !isMobile && (
          <VideoContainer
            className="previewPlayer"
            id={playerId.current}
            innerRef={playerContainerRef}
            zoom={zoom}
            data-nosnippet
          />
        )
      }
      {
        visible && (
          <MuteBtnContainer className="muteBtnContainer">
            <MuteButton onClick={muteHandler} muted={muted} />
          </MuteBtnContainer>
        )
      }
    </>
  );
}
